import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Facebook, Twitter, Instagram, Youtube, Globe, Save } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function SchoolSettings({ user }) {
  const [settings, setSettings] = useState({
    school_name: '',
    description: '',
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
    website: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API}/settings/school`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/settings/school`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Settings saved successfully!');
      } else {
        toast.error(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'teacher') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <p className="text-[#588157] mb-4">Only teachers can access settings</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>School Settings</h1>
          <p className="text-lg text-[#588157]">Manage your school information and social media links</p>
        </div>

        <Card className="p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <Label htmlFor="school_name" className="text-[#344E41]">School Name</Label>
              <Input
                id="school_name"
                type="text"
                value={settings.school_name}
                onChange={(e) => setSettings({ ...settings, school_name: e.target.value })}
                data-testid="school-name-input"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-[#344E41]">Description</Label>
              <Input
                id="description"
                type="text"
                value={settings.description}
                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                data-testid="school-description-input"
                className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
              />
            </div>

            <div className="border-t border-[#CCD5AE] pt-6 mt-6">
              <h3 className="text-xl font-bold text-[#344E41] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Social Media Links</h3>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="website" className="text-[#344E41] flex items-center gap-2">
                    <Globe className="w-4 h-4" strokeWidth={1.5} />
                    Website
                  </Label>
                  <Input
                    id="website"
                    type="url"
                    value={settings.website || ''}
                    onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                    placeholder="https://yourschool.com"
                    data-testid="website-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>

                <div>
                  <Label htmlFor="facebook" className="text-[#344E41] flex items-center gap-2">
                    <Facebook className="w-4 h-4" strokeWidth={1.5} />
                    Facebook
                  </Label>
                  <Input
                    id="facebook"
                    type="url"
                    value={settings.facebook || ''}
                    onChange={(e) => setSettings({ ...settings, facebook: e.target.value })}
                    placeholder="https://facebook.com/yourpage"
                    data-testid="facebook-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>

                <div>
                  <Label htmlFor="twitter" className="text-[#344E41] flex items-center gap-2">
                    <Twitter className="w-4 h-4" strokeWidth={1.5} />
                    Twitter
                  </Label>
                  <Input
                    id="twitter"
                    type="url"
                    value={settings.twitter || ''}
                    onChange={(e) => setSettings({ ...settings, twitter: e.target.value })}
                    placeholder="https://twitter.com/yourhandle"
                    data-testid="twitter-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>

                <div>
                  <Label htmlFor="instagram" className="text-[#344E41] flex items-center gap-2">
                    <Instagram className="w-4 h-4" strokeWidth={1.5} />
                    Instagram
                  </Label>
                  <Input
                    id="instagram"
                    type="url"
                    value={settings.instagram || ''}
                    onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
                    placeholder="https://instagram.com/yourhandle"
                    data-testid="instagram-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>

                <div>
                  <Label htmlFor="youtube" className="text-[#344E41] flex items-center gap-2">
                    <Youtube className="w-4 h-4" strokeWidth={1.5} />
                    YouTube
                  </Label>
                  <Input
                    id="youtube"
                    type="url"
                    value={settings.youtube || ''}
                    onChange={(e) => setSettings({ ...settings, youtube: e.target.value })}
                    placeholder="https://youtube.com/yourchannel"
                    data-testid="youtube-input"
                    className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="save-settings-button"
              className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg py-6 text-lg"
            >
              <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}